/* ==========================================================================
   GOODS SCENE -- PRODUCT DETAILS: PROGRESSIVE VIDEO ENHANCEMENT

   The server has already rendered the COMPLETE original product.description
   inside the Overview column. This script only ever MOVES existing iframe nodes
   into a second column. It never creates, clones, rewrites or deletes
   description content.

   🛑 WHY THIS IS NOT A LIQUID PARSER. A full audit of all 789 catalog
   descriptions found 65 distinct VIDEO-heading markup patterns, 28 descriptions
   with unclosed <div>s crossing the candidate split boundary, and 41 with an
   iframe nested inside a heading. Raw-string splitting cannot be made
   false-positive-proof against that. By the time this runs, the browser has
   already parsed and repaired the legacy HTML into a well-formed tree, so a
   node move is atomic and cannot unbalance anything.

   🛑 moveBefore() IS REQUIRED FOR EVERY IFRAME, AND THERE IS NO appendChild
   FALLBACK FOR ONE. Removing an <iframe> from the document discards its nested
   browsing context, so appendChild would make YouTube load a SECOND time on
   every page view. moveBefore() preserves that state -- in both directions,
   which is what makes the multi-video rollback below safe.

   ⚠️ moveBefore() ALSO REQUIRES BOTH NODES TO BE CONNECTED. An earlier build
   called it while the target frame was still a detached createElement() node;
   it threw HierarchyRequestError, the catch swallowed it, and every eligible
   product silently stayed in fallback. Hence, everywhere below:

       body.appendChild(column)   <- NEW empty nodes: nothing to preserve
       frame.moveBefore(iframe)   <- EXISTING iframe: state must survive

   ⚠️ appendChild IS CORRECT FOR NEW WRAPPERS AND FORBIDDEN FOR IFRAMES. Do not
   "simplify" them to match each other.

   🔑 FALSE NEGATIVE > FALSE POSITIVE, EVERYWHERE BELOW. Every ambiguous case
   returns early and leaves the description exactly as the server sent it.
   ========================================================================== */

(function () {
  if (customElements.get('gs-product-details')) return;

  var YT = /(?:^|\.)youtube(?:-nocookie)?\.com$/i;

  /* Presentational wrappers that may sit between a heading and its video, or
     inside a heading, without meaning anything on their own. */
  var HARMLESS = { BR: 1, STRONG: 1, B: 1, EM: 1, I: 1, SPAN: 1, U: 1, FONT: 1 };

  /* 🔑 THE LABEL TEXT IS NOT IN THIS FILE. It comes from the translation key
     products.product.gs_video_label, which the snippet publishes onto the host
     element as data-gs-video-label. ⚠️ This asset is served as a static file --
     Shopify never runs Liquid on it -- so a data attribute is the only channel
     by which a merchant-editable, per-locale string can reach this code.

     ⚠️ gs.js.product.video_label (window.gsT) is a LAST-RESORT FALLBACK, not the
     source of truth. It only applies if the attribute is missing or blank, which
     would otherwise leave an icon with no accessible name. */
  function videoLabelFallback() {
    return typeof window.gsT === 'function' ? window.gsT('product.video_label') : '';
  }

  var VIDEO_ICON_HTML =
    '<span class="gs-product-details__icon" aria-hidden="true">' +
    '<svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.4">' +
    '<circle cx="8" cy="8" r="6.4"/><path d="M6.5 5.4l4.2 2.6-4.2 2.6z" stroke-linejoin="round"/>' +
    '</svg></span>';

  function videoLabelText(host) {
    var v = host && host.dataset ? host.dataset.gsVideoLabel : '';
    v = (v || '').trim();
    return v || videoLabelFallback();
  }

  /* 🛑 THE LABEL IS APPENDED AS A TEXT NODE, NEVER CONCATENATED INTO innerHTML.
     The string is merchant-editable through the language editor, so building it
     into markup would make a translation an HTML injection point. innerHTML is
     used only for the fixed icon. */
  function applyVideoLabel(h, host) {
    h.innerHTML = VIDEO_ICON_HTML;
    h.appendChild(document.createTextNode(videoLabelText(host)));
  }

  function normalized(el) {
    return (el.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /* 🔑 THE VIDEO MARKER, LANGUAGE-SAFE. Description headings are translated, so the
     token is matched with accents folded (FR "VIDÉO" -> VIDEO) and at a WORD START
     only (DE compounds "VIDEOANLEITUNG", "VIDEO-DEMONSTRATION"). Checked against all
     697 video products: EN matches are unchanged, and DE/FR now find exactly the
     same headings as EN. The VIEDEO typo still never matches. */
  function hasVideoToken(el) {
    return /\bVIDEO/i.test(normalized(el).normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
  }

  function isYouTube(iframe) {
    try {
      return YT.test(new URL(iframe.src, window.location.href).hostname);
    } catch (e) {
      return false;
    }
  }

  function isIgnorable(node, skipEl) {
    if (node === skipEl) return true;
    if (node.nodeType === Node.TEXT_NODE) return !node.textContent.trim();
    if (node.nodeType === Node.COMMENT_NODE) return true;
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    if (node.contains && skipEl && node.contains(skipEl)) {
      return !normalized(node) && onlyContains(node, skipEl);
    }
    if (!HARMLESS[node.tagName] && node.tagName !== 'P' && node.tagName !== 'DIV') return false;
    return !normalized(node) && !node.querySelector('img, iframe, a, table, ul, ol');
  }

  function onlyContains(root, el) {
    var walk = root.querySelectorAll('*');
    for (var i = 0; i < walk.length; i++) {
      if (walk[i] !== el && !HARMLESS[walk[i].tagName] && walk[i].tagName !== 'P' && walk[i].tagName !== 'DIV') {
        return false;
      }
    }
    return true;
  }

  /* Records where a node currently lives so a failed transaction can put it back
     with moveBefore -- preserving the browsing context on the way back too. */
  function anchorOf(node) {
    return { parent: node.parentNode, next: node.nextSibling };
  }

  /* Walks up from a just-emptied position and removes wrappers that now hold
     nothing. ⚠️ Bounded and conservative: stops at the Overview root, stops at
     anything carrying real text or a meaningful element, and never climbs more
     than a few levels. Only ever called AFTER a transaction has committed. */
  function cleanEmptyWrappers(node, root) {
    var guard = 0;
    while (node && node !== root && guard++ < 5) {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (!/^(H[1-6]|P|SPAN|STRONG|B|EM|I|DIV)$/.test(node.tagName)) return;
      if (normalized(node)) return;
      if (node.querySelector('a, img, iframe, table, ul, ol, video, audio')) return;
      var parent = node.parentNode;
      node.remove();
      node = parent;
    }
  }

  function headingSafeToEmpty(h3, iframe) {
    if (h3.querySelector('a, img, table, ul, ol')) return false;
    return hasVideoToken(h3);
  }

  /* ⚠️ Walks nextSibling in DOCUMENT ORDER and never descends into the heading,
     so <strong>/<br> inside the VIDEO H3 are never examined. Verified against
     the live DOM: this correctly returns true for <h3>…</h3><p><iframe></p>. */
  function nothingMeaningfulBetween(h3, iframe) {
    var node = h3;
    var guard = 0;
    while (node && guard++ < 400) {
      if (node.nextSibling) {
        node = node.nextSibling;
      } else {
        node = node.parentNode;
        if (!node) return false;
        continue;
      }
      if (node === iframe || (node.contains && node.contains(iframe))) {
        return node === iframe || isIgnorable(node, iframe);
      }
      if (node.nodeType === Node.ELEMENT_NODE && /^H[1-6]$/.test(node.tagName)) return false;
      if (!isIgnorable(node, iframe)) return false;
    }
    return false;
  }

  /* 🛑 THE DETECTION CONTRACT IS UNCHANGED AND MUST STAY THAT WAY. It still keys on
     the correctly spelled token VIDEO inside description headings -- those
     headings remain STRUCTURAL MARKERS. They are never shown to the customer;
     the visible label always comes from the translation above. */
  function associate(heading, iframe) {
    if (heading.contains(iframe)) {
      return headingSafeToEmpty(heading, iframe) ? 'inside' : null;
    }
    if (heading.compareDocumentPosition(iframe) !== Node.DOCUMENT_POSITION_FOLLOWING) return null;
    return nothingMeaningfulBetween(heading, iframe) ? 'before' : null;
  }

  class GsProductDetails extends HTMLElement {
    connectedCallback() {
      /* data-gs-enhanced means "this element has been PROCESSED", not "a split
         happened". Split styling is driven separately by data-gs-pd-split on
         the body. A Theme Editor section reload delivers fresh markup with no
         marker and processes once. */
      if (this.dataset.gsEnhanced === 'true') return;

      var overview = this.querySelector('[data-gs-pd-overview]');
      if (!overview) return;

      /* 🛑 RUNS FIRST, AND UNCONDITIONALLY. The card supplies its own OVERVIEW
         subheading, so a legacy leading OVERVIEW H3 reads twice. Cleanup must
         not depend on video eligibility. */
      this.removeLeadingOverview(overview);

      try {
        this.enhance(overview);
      } catch (e) {
        /* Any failure leaves the complete description exactly where it is. */
        if (window.console) console.warn('[gs-product-details] enhancement skipped:', e);
      }

      /* Marked last: an exception above must never leave a half-processed DOM
         flagged as done. */
      this.dataset.gsEnhanced = 'true';
    }

    /* Exact match only. Anything else -- "OVERVIEW & SPECS", a heading with a
       link, a different first heading -- is preserved untouched. */
    removeLeadingOverview(overview) {
      try {
        var first = overview.querySelector('h1, h2, h3, h4, h5, h6');
        if (!first) return;
        if (normalized(first).toUpperCase() !== 'OVERVIEW') return;
        if (first.querySelector('a, img, iframe, table, ul, ol')) return;
        first.remove();
      } catch (e) {
        if (window.console) console.warn('[gs-product-details] overview cleanup skipped:', e);
      }
    }

    enhance(overview) {
      var body = this.querySelector('[data-gs-pd-body]');
      if (!body) return;

      /* CONDITION E -- moveBefore or nothing. */
      if (typeof body.moveBefore !== 'function') return;

      /* CONDITION C -- author CSS inside the description would break when the
         iframe leaves the element that CSS targets. 14 products carry one. */
      if (overview.querySelector('style')) return;

      var frames = Array.prototype.filter.call(overview.querySelectorAll('iframe'), isYouTube);
      var allH3 = Array.prototype.filter.call(overview.querySelectorAll('h3'), hasVideoToken);

      /* 🔑 THE MULTI-VIDEO BRANCH IS ENTERED ONLY ON AN EXACT 2+2 SHAPE, and it
         returns unconditionally afterwards, so the single-video path below is
         never reached with multi-video input.

         ⚠️ THIS IS THE LINE THAT KEEPS 2-IFRAME / 1-HEADING PRODUCTS IN FALLBACK.
         One video would have no label and no deterministic owner. 2 !== 1 rejects
         them here, and they then fail `frames.length !== 1` below as well.
         Never relax this to >= 2. */
      if (frames.length === 2 && allH3.length === 2) {
        this.enhanceMulti(body, overview, frames, allH3);
        return;
      }

      /* CONDITION A -- exactly one YouTube iframe. */
      if (frames.length !== 1) return;
      var iframe = frames[0];

      /* CONDITION D -- a classed wrapper may be styled by app or theme CSS. */
      var parent = iframe.parentElement;
      if (parent && parent !== overview && parent.className) return;

      /* CONDITION B -- exactly one H3 whose text contains the correctly spelled
         token VIDEO. ⚠️ Deliberately strict: the catalog contains one VIEDEO
         typo, which simply never enhances. */
      if (allH3.length !== 1) return;
      var heading = allH3[0];

      var inside = heading.contains(iframe);
      if (inside) {
        if (!headingSafeToEmpty(heading, iframe)) return;
      } else {
        if (heading.compareDocumentPosition(iframe) !== Node.DOCUMENT_POSITION_FOLLOWING) return;
        if (!nothingMeaningfulBetween(heading, iframe)) return;
      }

      var column = this.buildVideoColumn();
      var frame = column.querySelector('[data-gs-pd-frame]');

      /* 🛑 STEP 1 -- CONNECT THE NEW COLUMN. appendChild is correct here: this
         node was created microseconds ago and has no browsing context, no
         listeners and no loading state to preserve. It also makes `frame` a
         connected node, which moveBefore below requires. */
      body.appendChild(column);

      /* 🛑 STEP 2 -- MOVE THE EXISTING IFRAME. moveBefore ONLY.
         ⚠️ THE DOM IS ALREADY MUTATED AT THIS POINT, so a throw here has to be
         rolled back or the page is left with an empty orphan Video column. */
      try {
        frame.moveBefore(iframe, null);
      } catch (e) {
        column.remove();
        if (window.console) console.warn('[gs-product-details] iframe move failed, rolled back:', e);
        return;
      }

      /* 🔑 THE COMMIT POINT. */
      body.setAttribute('data-gs-pd-split', 'true');

      if (!heading.querySelector('a, img, iframe, table, ul, ol')) {
        heading.remove();
      }
    }

    /* 🛑 ALL-OR-NOTHING. Some products intentionally carry two distinct videos.
       Either both move or neither does: a product must never be left with one
       video in the right column and the other stranded in Overview.

       🔑 RESTORE ANCHORS ARE CAPTURED BEFORE ANY MUTATION, and the rollback puts
       iframe #1 back with moveBefore too -- so even the undo preserves its
       browsing context and YouTube never reloads. */
    enhanceMulti(body, overview, frames, headings) {
      if (typeof body.moveBefore !== 'function') return;
      if (overview.querySelector('style')) return;

      /* Pair each heading with the first iframe it legitimately owns, using the
         SAME association rule as the single-video branch. */
      var pairs = [];
      for (var i = 0; i < headings.length; i++) {
        var match = null;
        for (var j = 0; j < frames.length; j++) {
          if (pairs.some(function (p) { return p.iframe === frames[j]; })) continue;
          if (associate(headings[i], frames[j])) { match = frames[j]; break; }
        }
        if (!match) return;
        var wrap = match.parentElement;
        if (wrap && wrap !== overview && wrap.className) return;
        pairs.push({ heading: headings[i], iframe: match });
      }
      if (pairs.length !== 2) return;
      if (pairs[0].iframe === pairs[1].iframe) return;

      /* 🛑 ANCHORS FIRST. Captured while everything is still untouched. */
      pairs.forEach(function (p) { p.anchor = anchorOf(p.iframe); p.wrapper = p.iframe.parentElement; });

      var host = this;
      var column = this.buildVideoColumn(true);

      /* 🔑 EACH ITEM IS A FRAME ONLY -- NO PER-VIDEO HEADING. buildVideoColumn()
         above already created the single shared label for the whole column. An
         earlier build labelled every item and rendered "Video" twice. */
      var items = pairs.map(function (p) {
        var item = document.createElement('div');
        item.className = 'gs-product-details__video-item';
        var frame = document.createElement('div');
        frame.className = 'gs-product-details__frame';
        item.appendChild(frame);
        column.appendChild(item);
        return frame;
      });

      /* New, state-free nodes: appendChild is correct and makes the frames
         connected, which moveBefore requires. */
      body.appendChild(column);

      var moved = 0;
      try {
        items[0].moveBefore(pairs[0].iframe, null); moved = 1;
        items[1].moveBefore(pairs[1].iframe, null); moved = 2;
      } catch (e) {
        /* 🛑 ROLLBACK. Put any already-moved iframe back exactly where it was. */
        try {
          if (moved === 1) {
            pairs[0].anchor.parent.moveBefore(pairs[0].iframe, pairs[0].anchor.next);
          }
        } catch (e2) {
          if (window.console) console.warn('[gs-product-details] rollback failed:', e2);
        }
        column.remove();
        if (window.console) console.warn('[gs-product-details] multi-video move failed, rolled back:', e);
        return;
      }

      /* 🔑 COMMIT. Both moves succeeded; only now is anything removed. */
      body.setAttribute('data-gs-pd-split', 'true');
      body.setAttribute('data-gs-pd-multi', 'true');

      pairs.forEach(function (p) {
        if (!p.heading.querySelector('a, img, iframe, table, ul, ol')) p.heading.remove();
        cleanEmptyWrappers(p.wrapper, overview);
      });
    }

    /* 🔑 ONE VIDEO LABEL PER COLUMN, IN BOTH MODES. An earlier build let the multi
       branch create a label per video, which rendered "Video" twice above two
       videos. The heading is built here, BEFORE the multi return, so there is
       exactly one applyVideoLabel() call on any product.

       ⚠️ THE multi RETURN MUST STAY. A multi column must not receive the shared
       single-video frame: each video item builds its own, and a stray empty
       .gs-product-details__frame would render as a full-width 16:9 empty box. */
    buildVideoColumn(multi) {
      var col = document.createElement('div');
      col.className = 'gs-product-details__col gs-product-details__col--video';
      col.setAttribute('role', 'region');
      col.setAttribute('aria-labelledby', 'GsVideoHeading');

      var h = document.createElement('h2');
      h.className = 'gs-product-details__subhead';
      h.id = 'GsVideoHeading';
      applyVideoLabel(h, this);
      col.appendChild(h);

      if (multi) return col;

      var frame = document.createElement('div');
      frame.className = 'gs-product-details__frame';
      frame.setAttribute('data-gs-pd-frame', '');
      col.appendChild(frame);
      return col;
    }
  }

  customElements.define('gs-product-details', GsProductDetails);
})();
